import PropTypes from "prop-types";
import { Box, Typography, Stack } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

// ─── Card configuration ───────────────────────────────────────────────────────
// index matches statusTabValue: 0=All, 1=Pending, 2=In Process, 3=Approved, 4=Rejected
const CARD_CONFIGS = [
  { index: 0, label: "All",        statusKey: "all",        paletteKey: null     },
  { index: 1, label: "Pending",    statusKey: "pending",    paletteKey: "warning" },
  { index: 2, label: "In Process", statusKey: "in process", paletteKey: "info"   },
  { index: 3, label: "Approved",   statusKey: "approved",   paletteKey: "success" },
  { index: 4, label: "Rejected",   statusKey: "rejected",   paletteKey: "error"  },
];

// ─── Single card ──────────────────────────────────────────────────────────────
function StatusCard({ config, count, isSelected, onClick }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Resolve accent, bg, and text colours from the MUI palette.
  // The "All" card uses grey; every other card uses its status palette entry.
  let accentColor;
  let textColor;

  if (!config.paletteKey) {
    // "All" → grey
    accentColor = theme.palette.grey[500];
    textColor   = isDark ? theme.palette.grey[200] : theme.palette.grey[800];
  } else {
    const entry = theme.palette[config.paletteKey];
    accentColor = entry.main;
    // Use 800-900 shade in light mode, 100-200 shade in dark mode for legibility
    textColor = isDark ? entry.light : entry.dark;
  }

  const bgBase     = alpha(accentColor, isDark ? 0.15 : 0.08);
  const bgHover    = alpha(accentColor, isDark ? 0.24 : 0.14);
  const bgSelected = alpha(accentColor, isDark ? 0.28 : 0.18);

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      aria-label={`Filter by ${config.label}`}
      onClick={onClick}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick()}
      sx={{
        flex: "1 1 0",
        minWidth: { xs: "calc(50% - 6px)", sm: 0 },
        p: { xs: 1.5, sm: 2 },
        borderRadius: 2,
        cursor: "pointer",
        userSelect: "none",
        backgroundColor: isSelected ? bgSelected : bgBase,
        border: isSelected
          ? `2px solid ${accentColor}`
          : "2px solid transparent",
        outline: "none",
        transition: "background-color 150ms ease, border-color 150ms ease",
        "&:hover": {
          backgroundColor: bgHover,
        },
        "&:focus-visible": {
          outline: `2px solid ${accentColor}`,
          outlineOffset: 2,
        },
      }}
    >
      {/* Status label — small, uppercase, above the count */}
      <Typography
        component="span"
        sx={{
          display: "block",
          fontSize: "0.68rem",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.6px",
          color: textColor,
          lineHeight: 1.2,
          mb: 0.75,
          opacity: 0.85,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {config.label}
      </Typography>

      {/* Count — large, bold */}
      <Typography
        component="span"
        sx={{
          display: "block",
          fontSize: { xs: "1.6rem", sm: "1.9rem" },
          fontWeight: 800,
          color: textColor,
          lineHeight: 1,
        }}
      >
        {count ?? "–"}
      </Typography>
    </Box>
  );
}

StatusCard.propTypes = {
  config:     PropTypes.shape({
    index:     PropTypes.number.isRequired,
    label:     PropTypes.string.isRequired,
    statusKey: PropTypes.string.isRequired,
    paletteKey: PropTypes.string,
  }).isRequired,
  count:      PropTypes.number,
  isSelected: PropTypes.bool.isRequired,
  onClick:    PropTypes.func.isRequired,
};

// ─── Card row ─────────────────────────────────────────────────────────────────
export default function StatusSummaryCards({ counts, selectedIndex, onSelect, sx }) {
  return (
    <Stack
      direction="row"
      spacing={{ xs: 1, sm: 1.5 }}
      sx={{
        width: "100%",
        flexWrap: { xs: "wrap", sm: "nowrap" },
        mb: 3,
        ...sx,
      }}
    >
      {CARD_CONFIGS.map((config) => (
        <StatusCard
          key={config.index}
          config={config}
          count={counts?.[config.statusKey]}
          isSelected={selectedIndex === config.index}
          onClick={() => onSelect(config.index)}
        />
      ))}
    </Stack>
  );
}

StatusSummaryCards.propTypes = {
  /** Status counts keyed by statusKey (all / pending / in process / approved / rejected). */
  counts: PropTypes.shape({
    all:           PropTypes.number,
    pending:       PropTypes.number,
    "in process":  PropTypes.number,
    approved:      PropTypes.number,
    rejected:      PropTypes.number,
  }),
  /** Index of the currently selected card (0 = All, 1 = Pending, …). */
  selectedIndex: PropTypes.number.isRequired,
  /** Called with the card index when a card is clicked. */
  onSelect:      PropTypes.func.isRequired,
  sx:            PropTypes.object,
};
